/**
 * M-H2 — Hive Skill Inheritance preview modal.
 *
 * Sprint 133 Stream C. Lets the operator pick a member agent of the hive
 * and see exactly which skills will apply to that agent at runtime,
 * showing source (hive / agent / agent_overrides_hive) and the
 * effective binding values.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { useHiveInheritancePreview } from '@/features/skills';
import { useI18n } from '@/i18n';
import type { HiveInheritanceSource } from '@/features/skills';

interface HiveMemberRef {
  id: string;
  name?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  hiveId: string;
  members: HiveMemberRef[];
}

export function HiveSkillInheritanceModal({
  open,
  onOpenChange,
  hiveId,
  members,
}: Props) {
  const { t } = useI18n();
  const [agentId, setAgentId] = useState<string>('');

  const SOURCE_VARIANT: Record<HiveInheritanceSource, { label: string; tone: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    hive: { label: t('admin.agents.hive.skillInheritance.sourceInherited'), tone: 'secondary' },
    agent: { label: t('admin.agents.hive.skillInheritance.sourceAgent'), tone: 'outline' },
    agent_overrides_hive: { label: t('admin.agents.hive.skillInheritance.sourceOverride'), tone: 'destructive' },
  };

  useEffect(() => {
    if (open && members.length > 0 && !agentId) {
      setAgentId(members[0].id);
    }
    if (!open) {
      setAgentId('');
    }
  }, [open, members, agentId]);

  const { data, isLoading, isError, error } = useHiveInheritancePreview(
    hiveId,
    agentId || undefined,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('admin.agents.hive.skillInheritance.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.agents.hive.skillInheritance.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label>{t('admin.agents.hive.skillInheritance.memberAgent')}</Label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {members.length === 0 && <option value="">{t('admin.agents.hive.skillInheritance.noMembers')}</option>}
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? m.id}
                </option>
              ))}
            </select>
          </div>

          <div className="border rounded-md min-h-[200px]">
            {!agentId ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                {t('admin.agents.hive.skillInheritance.selectMember')}
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner />
              </div>
            ) : isError ? (
              <div className="text-center py-10 text-sm text-destructive">
                {(error as Error)?.message ?? t('admin.agents.hive.skillInheritance.loadError')}
              </div>
            ) : !data || data.items.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                {t('admin.agents.hive.skillInheritance.noSkills')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('admin.agents.hive.skillInheritance.colSkill')}</th>
                    <th className="px-3 py-2 font-medium">{t('admin.agents.hive.skillInheritance.colSource')}</th>
                    <th className="px-3 py-2 font-medium">{t('admin.agents.hive.skillInheritance.colMode')}</th>
                    <th className="px-3 py-2 font-medium">{t('admin.agents.hive.skillInheritance.colPriority')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((it) => {
                    const src = SOURCE_VARIANT[it.source];
                    return (
                      <tr key={it.skill_id}>
                        <td className="px-3 py-2">
                          <div className="font-medium">
                            {it.name ?? it.slug}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {it.slug}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={src.tone}>{src.label}</Badge>
                        </td>
                        <td className="px-3 py-2 font-mono">
                          {it.effective.mode}
                        </td>
                        <td className="px-3 py-2 font-mono">
                          {it.effective.priority}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HiveSkillInheritanceModal;
